export const EMBEDDED_CAROUSEL_THEME_QUERY_KEYS = [
  'theme.bodyBg',
  'theme.text',
  'theme.primary',
  'theme.buttonText',
  'theme.surface',
  'theme.border',
  'theme.focus',
  'theme.spacing',
  'theme.radius',
] as const;

const THEME_PREFIX = 'theme.';
const COLOR_PATTERN = /^#[0-9A-Fa-f]{6}$/;
const MAX_CANONICAL_THEME_BYTES = 384;
const MAX_STYLED_ROUTE_ROOT_LENGTH = 1024;
const MAX_STYLED_REQUEST_URL_LENGTH = 2048;

const COLOR_KEYS = [
  'theme.bodyBg',
  'theme.text',
  'theme.primary',
  'theme.buttonText',
  'theme.surface',
  'theme.border',
  'theme.focus',
] as const;

export type EmbeddedCarouselThemeSpacing = 'compact' | 'normal' | 'relaxed';
export type EmbeddedCarouselThemeRadius = 'lg' | 'md' | 'none' | 'sm';

export type EmbeddedCarouselTheme = {
  bodyBg?: string;
  text?: string;
  primary?: string;
  buttonText?: string;
  surface?: string;
  border?: string;
  focus?: string;
  spacing?: EmbeddedCarouselThemeSpacing;
  radius?: EmbeddedCarouselThemeRadius;
};

export type EmbeddedCarouselThemeInvalidReason =
  | 'empty-value'
  | 'invalid-color'
  | 'invalid-hide-link'
  | 'invalid-radius'
  | 'invalid-route-root'
  | 'invalid-spacing'
  | 'repeated-value'
  | 'request-url-too-long'
  | 'route-root-too-long'
  | 'theme-too-long'
  | 'unknown-key';

export type EmbeddedCarouselThemeParseResult =
  | { kind: 'absent' }
  | { kind: 'invalid'; reason: EmbeddedCarouselThemeInvalidReason }
  | { kind: 'valid'; theme: EmbeddedCarouselTheme };

export type EmbeddedCarouselThemeParseOptions = {
  iframeRequest?: {
    cName: string;
    carouselId: string;
  };
};

type ThemeQueryKey = typeof EMBEDDED_CAROUSEL_THEME_QUERY_KEYS[number];
type Query = Record<string, unknown>;

const allowedKeys = new Set<string>(EMBEDDED_CAROUSEL_THEME_QUERY_KEYS);
const spacingValues = new Set<EmbeddedCarouselThemeSpacing>(['compact', 'normal', 'relaxed']);
const radiusValues = new Set<EmbeddedCarouselThemeRadius>(['none', 'sm', 'md', 'lg']);

const queryKeyToThemeKey: Record<ThemeQueryKey, keyof EmbeddedCarouselTheme> = {
  'theme.bodyBg': 'bodyBg',
  'theme.text': 'text',
  'theme.primary': 'primary',
  'theme.buttonText': 'buttonText',
  'theme.surface': 'surface',
  'theme.border': 'border',
  'theme.focus': 'focus',
  'theme.spacing': 'spacing',
  'theme.radius': 'radius',
};

const spacingTokens: Record<EmbeddedCarouselThemeSpacing, Readonly<Record<string, string>>> = {
  compact: {
    '--dcm-embedded-carousel-gutter': '16px',
    '--dcm-embedded-carousel-item-gap': '8px',
    '--dcm-embedded-carousel-items-padding-y': '12px',
    '--dcm-embedded-carousel-items-padding-x': '12px',
    '--dcm-embedded-carousel-card-padding': '12px',
  },
  normal: {
    '--dcm-embedded-carousel-gutter': '25px',
    '--dcm-embedded-carousel-item-gap': '12px',
    '--dcm-embedded-carousel-items-padding-y': '20px',
    '--dcm-embedded-carousel-items-padding-x': '16px',
    '--dcm-embedded-carousel-card-padding': '16px',
  },
  relaxed: {
    '--dcm-embedded-carousel-gutter': '32px',
    '--dcm-embedded-carousel-item-gap': '16px',
    '--dcm-embedded-carousel-items-padding-y': '24px',
    '--dcm-embedded-carousel-items-padding-x': '20px',
    '--dcm-embedded-carousel-card-padding': '20px',
  },
};

const radiusPixels: Record<EmbeddedCarouselThemeRadius, string> = {
  none: '0px',
  sm: '4px',
  md: '8px',
  lg: '12px',
};

function invalid(reason: EmbeddedCarouselThemeInvalidReason): EmbeddedCarouselThemeParseResult {
  return { kind: 'invalid', reason };
}

function isValidColor(value: string): boolean {
  return value.length === 7 && COLOR_PATTERN.test(value);
}

function getThemeQueryKeys(query: Query): string[] {
  return Object.keys(query).filter((key) => key.startsWith(THEME_PREFIX));
}

function hideLinkTransportValue(value: unknown): string | undefined {
  if (typeof value === 'string') {
    return value;
  }
  if (Array.isArray(value)) {
    return value.filter((entry): entry is string => typeof entry === 'string').join(',');
  }
  return undefined;
}

function canonicalRawThemeParams(query: Query): URLSearchParams {
  const params = new URLSearchParams();

  EMBEDDED_CAROUSEL_THEME_QUERY_KEYS.forEach((key) => {
    const value = query[key];
    if (typeof value === 'string') {
      params.append(key, value);
    }
  });

  return params;
}

export function parseEmbeddedCarouselThemeQuery(
  query: Query,
  options: EmbeddedCarouselThemeParseOptions = {}
): EmbeddedCarouselThemeParseResult {
  const themeKeys = getThemeQueryKeys(query);
  if (themeKeys.length === 0) {
    return { kind: 'absent' };
  }

  if (themeKeys.some((key) => !allowedKeys.has(key))) {
    return invalid('unknown-key');
  }

  if (themeKeys.some((key) => typeof query[key] !== 'string')) {
    return invalid('repeated-value');
  }

  const suppliedKeys = EMBEDDED_CAROUSEL_THEME_QUERY_KEYS.filter((key) => query[key] !== undefined);

  const rawParams = canonicalRawThemeParams(query);
  if (rawParams.toString().length > MAX_CANONICAL_THEME_BYTES) {
    return invalid('theme-too-long');
  }

  if (query.hideLink !== undefined && typeof query.hideLink !== 'string') {
    return invalid('invalid-hide-link');
  }

  const { routeRoot } = query;
  if (routeRoot !== undefined && typeof routeRoot !== 'string') {
    return invalid('invalid-route-root');
  }
  if (routeRoot && routeRoot.length > MAX_STYLED_ROUTE_ROOT_LENGTH) {
    return invalid('route-root-too-long');
  }

  if (suppliedKeys.some((key) => (query[key] as string).length === 0)) {
    return invalid('empty-value');
  }
  if (COLOR_KEYS.some((key) => query[key] !== undefined && !isValidColor(query[key] as string))) {
    return invalid('invalid-color');
  }

  const spacing = query['theme.spacing'];
  if (spacing !== undefined && !spacingValues.has(spacing as EmbeddedCarouselThemeSpacing)) {
    return invalid('invalid-spacing');
  }

  const radius = query['theme.radius'];
  if (radius !== undefined && !radiusValues.has(radius as EmbeddedCarouselThemeRadius)) {
    return invalid('invalid-radius');
  }

  const theme: EmbeddedCarouselTheme = {
    ...(query['theme.bodyBg'] ? { bodyBg: (query['theme.bodyBg'] as string).toUpperCase() } : {}),
    ...(query['theme.text'] ? { text: (query['theme.text'] as string).toUpperCase() } : {}),
    ...(query['theme.primary'] ? { primary: (query['theme.primary'] as string).toUpperCase() } : {}),
    ...(query['theme.buttonText'] ? { buttonText: (query['theme.buttonText'] as string).toUpperCase() } : {}),
    ...(query['theme.surface'] ? { surface: (query['theme.surface'] as string).toUpperCase() } : {}),
    ...(query['theme.border'] ? { border: (query['theme.border'] as string).toUpperCase() } : {}),
    ...(query['theme.focus'] ? { focus: (query['theme.focus'] as string).toUpperCase() } : {}),
    ...(spacing ? { spacing: spacing as EmbeddedCarouselThemeSpacing } : {}),
    ...(radius ? { radius: radius as EmbeddedCarouselThemeRadius } : {}),
  };

  if (
    options.iframeRequest &&
    embeddedCarouselThemeIframeRequestUrl(query, theme, options.iframeRequest).length > MAX_STYLED_REQUEST_URL_LENGTH
  ) {
    return invalid('request-url-too-long');
  }

  return { kind: 'valid', theme };
}

export function embeddedCarouselThemeIframeRequestUrl(
  query: Query,
  theme: EmbeddedCarouselTheme,
  { cName, carouselId }: { cName: string; carouselId: string }
): string {
  const params = new URLSearchParams({
    carouselId,
    routeRoot: String(query.routeRoot),
  });

  if (query.hideTitle === 'true') {
    params.append('hideTitle', 'true');
  }
  const hideLink = hideLinkTransportValue(query.hideLink);
  if (hideLink) {
    params.append('hideLink', hideLink);
  }
  appendEmbeddedCarouselThemeParams(params, theme);

  return `/embedded-menu/${cName}/carousels/${carouselId}?${params.toString()}`;
}

export function appendEmbeddedCarouselThemeParams(params: URLSearchParams, theme: EmbeddedCarouselTheme): void {
  EMBEDDED_CAROUSEL_THEME_QUERY_KEYS.forEach((queryKey) => {
    const value = theme[queryKeyToThemeKey[queryKey]];
    if (value !== undefined) {
      params.append(queryKey, value);
    }
  });
}

export function embeddedCarouselThemeToSearchParams(theme: EmbeddedCarouselTheme): URLSearchParams {
  const params = new URLSearchParams();
  appendEmbeddedCarouselThemeParams(params, theme);
  return params;
}

export function embeddedCarouselThemeToTokens(theme: EmbeddedCarouselTheme): Readonly<Record<string, string>> {
  const tokens: Record<string, string> = {};

  if (theme.bodyBg) {
    tokens['--dcm-color-body-bg'] = theme.bodyBg;
  }
  if (theme.text) {
    tokens['--dcm-color-text-primary'] = theme.text;
    tokens['--dcm-section-title-color'] = theme.text;
    tokens['--dcm-product-name-color'] = theme.text;
    tokens['--dcm-product-brand-color'] = theme.text;
    tokens['--dcm-product-price-color'] = theme.text;
    tokens['--dcm-embedded-carousel-card-name-color'] = theme.text;
    tokens['--dcm-embedded-carousel-card-brand-color'] = theme.text;
    tokens['--dcm-embedded-carousel-card-price-color'] = theme.text;
    tokens['--dcm-embedded-carousel-link-color'] = theme.text;
  }
  if (theme.primary) {
    tokens['--dcm-color-primary'] = theme.primary;
    tokens['--dcm-embedded-carousel-button-bg'] = theme.primary;
    tokens['--dcm-embedded-carousel-button-hover-bg'] = theme.primary;
    tokens['--dcm-embedded-carousel-button-color'] = theme.buttonText ?? '#FFFFFF';
  } else if (theme.buttonText) {
    tokens['--dcm-embedded-carousel-button-color'] = theme.buttonText;
  }
  if (theme.surface) {
    tokens['--dcm-color-surface'] = theme.surface;
    tokens['--dcm-embedded-carousel-card-bg'] = theme.surface;
  }
  if (theme.border) {
    tokens['--dcm-color-border'] = theme.border;
    tokens['--dcm-embedded-carousel-card-border-color'] = theme.border;
  }
  if (theme.focus) {
    tokens['--dcm-color-focus'] = theme.focus;
    tokens['--dcm-color-focus-shadow'] = `${theme.focus}66`;
  }
  if (theme.spacing) {
    Object.assign(tokens, spacingTokens[theme.spacing]);
  }
  if (theme.radius) {
    tokens['--dcm-embedded-carousel-card-radius'] = radiusPixels[theme.radius];
    tokens['--dcm-embedded-carousel-button-radius'] = radiusPixels[theme.radius];
  }

  return tokens;
}

export function embeddedCarouselThemeToCss(theme: EmbeddedCarouselTheme): string {
  const declarations = Object.entries(embeddedCarouselThemeToTokens(theme))
    .map(([name, value]) => `${name}:${value};`)
    .join('');
  return declarations ? `:root{${declarations}}` : '';
}
